import React, { useEffect } from 'react'

import { Box, Grid, Skeleton, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import Accordion from '../../../../../../../../shared/components/Accordion/Accordion.component'
import EmptyState from '../../../../../../../../shared/components/EmptyState/EmptyState.component'
import CancelOfferRequestButton from './components/CancelOfferRequestButton/CancelOfferRequestButton.component'
import ItemRequestCard from './components/ItemRequestCard/ItemRequestCard.component'
import PaymentDetailsCard from './components/PaymentDetailsCard/PaymentDetailsCard.component'
import ReleaseOfferRequestButton from './components/ReleaseOfferRequestButton/ReleaseOfferRequestButton.component'
import ShareOfferButton from './components/ShareOfferButton/ShareOfferButton.component'
import TripDetailsCard from './components/TripDetailsCard/TripDetailsCard.component'

import { resetCancelOfferRequestForAdmin } from '../../../../../../../../redux/states/admin/orders/cancelOfferRequestForAdmin/cancelOfferRequestForAdmin.slice'
import { getOfferRequestPaymentDetailsForAdmin } from '../../../../../../../../redux/states/admin/orders/getOfferRequestPaymentDetailsForAdmin/getOfferRequestPaymentDetailsForAdmin.slice'
import { GetOfferRequestPaymentDetailsForAdminRequestPayload } from '../../../../../../../../redux/states/admin/orders/getOfferRequestPaymentDetailsForAdmin/getOfferRequestPaymentDetailsForAdmin.types'
import { resetReleaseOfferRequestForAdmin } from '../../../../../../../../redux/states/admin/orders/releaseOfferRequestForAdmin/releaseOfferRequestForAdmin.slice'
import { RootState } from '../../../../../../../../redux/store/store.types'
import { joinStringsWithBullet } from '../../../../../../../../shared/functions/String/string.functions'
import { useAppDispatch } from '../../../../../../../../shared/hooks/redux.hooks'
import styles from './OrderDetails.styles'
import { OrderDetailsProps } from './OrderDetails.types'

const OrderDetails: React.FC<OrderDetailsProps> = (props) => {
  const { offerRequestId } = props

  const dispatch = useAppDispatch()

  const {
    loading: itemRequestsLoading,
    success: itemRequestsSuccess,
    error: itemRequestsError
  } = useSelector((state: RootState) => state.getAllItemRequestsForAdmin)

  const {
    loading: paymentDetailsLoading,
    success: paymentDetailsSuccess,
    error: paymentDetailsError
  } = useSelector((state: RootState) => state.getOfferRequestPaymentDetailsForAdmin)

  const {
    loading: tripDetailsLoading,
    success: tripDetailsSuccess,
    error: tripDetailsError
  } = useSelector((state: RootState) => state.getOfferRequestTripDetailsForAdmin)

  const { success: cancelOfferRequestSuccess } = useSelector((state: RootState) => state.cancelOfferRequestForAdmin)

  useEffect(() => {
    if (cancelOfferRequestSuccess) {
      dispatch(resetCancelOfferRequestForAdmin())
      dispatch(resetReleaseOfferRequestForAdmin())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRefreshData = () => {
    if (offerRequestId) {
      const paymentDetailsPayload: GetOfferRequestPaymentDetailsForAdminRequestPayload = {
        request: { offerRequestId }
      }

      dispatch(getOfferRequestPaymentDetailsForAdmin(paymentDetailsPayload))
    }
  }

  const renderItemRequests = () => {
    if (itemRequestsLoading) {
      return <Skeleton variant="rectangular" sx={styles.skeletonCard} height={850} />
    } else {
      let content = null
      let title = 'Item Requests'
      let headerActions = null

      if (itemRequestsSuccess?.data.itemRequests) {
        title = joinStringsWithBullet([
          'Item Requests',
          itemRequestsSuccess?.data.itemRequests.length?.toString()
        ]) as string

        content = (
          <Box sx={styles.contentContainer}>
            {itemRequestsSuccess.data.itemRequests.map((itemRequest) => (
              <ItemRequestCard key={itemRequest.id} itemRequest={itemRequest} />
            ))}
          </Box>
        )

        if (offerRequestId) {
          const isOfferAcceptedByTraveler = !!tripDetailsSuccess?.data.traveler

          headerActions = (
            <Box sx={styles.headerActionsContainer}>
              {isOfferAcceptedByTraveler && (
                <ReleaseOfferRequestButton offerRequestId={offerRequestId} onSuccess={handleRefreshData} />
              )}

              <ShareOfferButton offerRequestId={offerRequestId} itemRequests={itemRequestsSuccess.data.itemRequests} />

              <CancelOfferRequestButton
                offerRequestId={offerRequestId}
                itemRequests={itemRequestsSuccess.data.itemRequests}
                onSuccess={handleRefreshData}
              />
            </Box>
          )
        }
      } else if (itemRequestsError) {
        content = (
          <EmptyState
            title="No item requests found"
            subtitle="No item requests found"
            errorId={itemRequestsError.correlationId}
          />
        )
      }

      return (
        <Accordion
          header={{
            mainContent: (
              <Box sx={styles.headerContainer}>
                <Typography variant="subtitle1">{title}</Typography>
                {headerActions}
              </Box>
            )
          }}
          body={content}
          isExpanded={true}
          preventExpandChange
          noExpandIcon
        />
      )
    }

    return null
  }

  const renderTripDetails = () => {
    if (tripDetailsLoading) {
      return <Skeleton variant="rectangular" sx={styles.skeletonCard} height={50} />
    } else {
      let content = null

      if (tripDetailsSuccess?.data) {
        content = (
          <Box sx={styles.contentContainer}>
            <TripDetailsCard tripDetails={tripDetailsSuccess.data} />
          </Box>
        )
      } else if (tripDetailsError) {
        content = (
          <EmptyState
            title="No trip details found"
            subtitle="No traveler has accepted this offer has yet"
            errorId={tripDetailsError.correlationId}
          />
        )
      }

      return (
        <Accordion
          header={{
            mainContent: 'Trip Details'
          }}
          body={content}
          isExpanded={false}
        />
      )
    }
  }

  const renderPaymentDetails = () => {
    if (paymentDetailsLoading) {
      return <Skeleton variant="rectangular" sx={styles.skeletonCard} height={850} />
    } else {
      let content = null

      if (paymentDetailsSuccess?.data) {
        const paymentDetails = paymentDetailsSuccess.data

        content = (
          <Box sx={styles.contentContainer}>
            <PaymentDetailsCard paymentDetails={paymentDetails} service="order" />
          </Box>
        )
      } else if (paymentDetailsError) {
        content = (
          <EmptyState
            title="No payment details found"
            subtitle="No payment details found"
            errorId={paymentDetailsError.correlationId}
          />
        )
      }

      return (
        <Accordion
          header={{
            mainContent: 'Payment Details'
          }}
          body={content}
          isExpanded={true}
        />
      )
    }
  }

  return (
    <Box sx={styles.container}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6.5}>
          {renderItemRequests()}
        </Grid>

        <Grid item xs={12} md={5.5}>
          {renderTripDetails()}
          {renderPaymentDetails()}
        </Grid>
      </Grid>
    </Box>
  )
}

export default OrderDetails
